// Dates and times in the interface language, in the office's time zone.
//
// format.ts stays the formatter for amounts (always Dutch notation) and for the
// screens not translated yet; these are for the screens that are.

import { intlTag, type Locale, type Translate } from './index.tsx';

const TIME_ZONE = 'Europe/Amsterdam';

/** "Maandag 2 november" / "Monday 2 November": only the first letter capitalised. */
export function longDay(now: Date, locale: Locale): string {
  const text = new Intl.DateTimeFormat(intlTag(locale), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: TIME_ZONE,
  }).format(now);
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** A yyyy-mm-dd date as "31 jan" / "31 Jan". */
export function shortDate(day: string, locale: Locale): string {
  const [year, month, date] = day.split('-').map(Number);
  return new Intl.DateTimeFormat(intlTag(locale), { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, date ?? 1)))
    .replace('.', '');
}

/** "zojuist", "5 minuten geleden", "3 uur geleden"; older: the date and time. */
export function relativeTime(iso: string, locale: Locale, t: Translate, now: Date = new Date()): string {
  const then = new Date(iso);
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (seconds < 60) return t('time.justNow');
  const relative = new Intl.RelativeTimeFormat(intlTag(locale), { numeric: 'auto' });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return relative.format(-minutes, 'minute');
  const hours = Math.round(minutes / 60);
  if (hours < 24) return relative.format(-hours, 'hour');
  return new Intl.DateTimeFormat(intlTag(locale), {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: TIME_ZONE,
  }).format(then);
}
